import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Upload, FileArchive, Database, AlertTriangle, CheckCircle2, XCircle,
  Trash2, PlayCircle, FileText, Loader2
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/formatUtils';

const fmtBytes = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const MigrationPage = () => {
  const { hasPermission } = useAuth();
  const [stage, setStage] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [truncate, setTruncate] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [status, setStatus] = useState({ status: 'idle', phases: [] });
  const [reports, setReports] = useState([]);
  const sqlInputRef = useRef(null);
  const zipInputRef = useRef(null);
  const pollRef = useRef(null);
  const [uploading, setUploading] = useState('');

  const fetchStage = useCallback(async () => {
    try {
      const r = await api.get('/migration/stage');
      if (r.data?.success) setStage(r.data.data);
    } catch { /* ignored */ }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get('/migration/status');
      if (r.data?.success) setStatus(r.data.data);
    } catch { /* ignored */ }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      const r = await api.get('/migration/reports');
      if (r.data?.success) setReports(r.data.data || []);
    } catch { /* ignored */ }
  }, []);

  useEffect(() => {
    fetchStage();
    fetchStatus();
    fetchReports();
  }, [fetchStage, fetchStatus, fetchReports]);

  // Poll status while running.
  useEffect(() => {
    if (status.status === 'running') {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchStatus, 1500);
      return () => clearInterval(pollRef.current);
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (status.status === 'done' || status.status === 'error') {
      fetchReports();
    }
    return undefined;
  }, [status.status, fetchStatus, fetchReports]);

  const upload = async (file, kind) => {
    if (!file) return;
    setUploading(kind);
    const form = new FormData();
    form.append('file', file);
    try {
      await api.post(`/migration/${kind === 'sql' ? 'upload-sql' : 'upload-photos'}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${kind === 'sql' ? 'SQL dump' : 'Photo zip'} uploaded`);
      await fetchStage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading('');
    }
  };

  const clearStage = async () => {
    if (!window.confirm('Remove staged files (SQL + zip)?')) return;
    await api.delete('/migration/stage');
    await fetchStage();
    toast.success('Staged files cleared');
  };

  const start = async () => {
    if (!stage?.sql_uploaded) {
      toast.error('Upload the legacy .sql dump first');
      return;
    }
    if (!dryRun && truncate && confirmText !== 'MIGRATE') {
      toast.error('Type MIGRATE in the confirm box to wipe existing data');
      return;
    }
    const form = new FormData();
    form.append('dry_run', dryRun);
    form.append('truncate', truncate);
    form.append('confirm_token', confirmText);
    try {
      await api.post('/migration/run', form);
      setStatus((s) => ({ ...s, status: 'running', phases: [] }));
      toast.success(dryRun ? 'Dry-run started' : 'Live migration started');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start migration');
    }
  };

  if (!hasPermission('migration.run')) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            You don't have permission to access the Legacy Migration tool.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="migration-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Legacy Data Migration</h1>
        <p className="text-sm text-muted-foreground mt-1">
          One-time tool to import data from the old PHP/MySQL Myzow ERP into this system.
          Use dry-run first to preview counts, then a live run with truncate to commit.
        </p>
      </div>

      {/* Danger banner */}
      <Card className="border-amber-500/40 bg-amber-50 dark:bg-amber-950/20">
        <CardContent className="p-4 flex gap-3 items-start">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium">Live runs with “truncate” are destructive.</div>
            <div className="text-muted-foreground mt-1">
              Every record in <code>clients</code>, <code>projects</code>, <code>invoices</code>,
              <code> payments</code>, <code>expenses</code>, <code>smb_leads</code>, and chat
              sessions will be deleted before the legacy data is loaded. Users, roles, HR data,
              and settings are <strong>not</strong> touched. Always run a dry-run first.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 1 — Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Step 1 · Upload files</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* SQL */}
            <div className="border rounded-md p-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Database className="h-4 w-4" /> Legacy MySQL Dump (.sql)
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Required. Should contain <code>projects</code>, <code>sub_projects</code>,
                <code>invoices</code>, <code>trans</code>, <code>myzow_local_leads</code>,
                and <code>myzow_local_lead_photos</code>.
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input
                  ref={sqlInputRef}
                  type="file"
                  accept=".sql"
                  className="hidden"
                  data-testid="sql-file-input"
                  onChange={(e) => upload(e.target.files?.[0], 'sql')}
                />
                <Button
                  variant="outline" size="sm"
                  onClick={() => sqlInputRef.current?.click()}
                  disabled={uploading === 'sql'}
                  data-testid="upload-sql-btn"
                >
                  {uploading === 'sql'
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…</>
                    : <><Upload className="h-4 w-4 mr-2" /> Choose .sql</>}
                </Button>
                {stage?.sql_uploaded && (
                  <Badge variant="secondary" data-testid="sql-staged-badge">
                    <CheckCircle2 className="h-3 w-3 mr-1 text-green-600" />
                    Staged · {fmtBytes(stage.sql_size)}
                  </Badge>
                )}
              </div>
            </div>

            {/* ZIP */}
            <div className="border rounded-md p-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <FileArchive className="h-4 w-4" /> SMB Lead Photos (.zip) <span className="text-xs font-normal text-muted-foreground">— optional</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Zip of legacy <code>_photo_*.jpg</code> files. Filenames must start with the
                lead id (e.g. <code>50_photo_xxxxxxxx.jpg</code>) so we know which lead they
                belong to. Skip if you don't have them — leads will migrate without photos.
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input
                  ref={zipInputRef}
                  type="file"
                  accept=".zip"
                  className="hidden"
                  data-testid="zip-file-input"
                  onChange={(e) => upload(e.target.files?.[0], 'zip')}
                />
                <Button
                  variant="outline" size="sm"
                  onClick={() => zipInputRef.current?.click()}
                  disabled={uploading === 'zip'}
                  data-testid="upload-zip-btn"
                >
                  {uploading === 'zip'
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…</>
                    : <><Upload className="h-4 w-4 mr-2" /> Choose .zip</>}
                </Button>
                {stage?.zip_uploaded && (
                  <Badge variant="secondary" data-testid="zip-staged-badge">
                    <CheckCircle2 className="h-3 w-3 mr-1 text-green-600" />
                    Staged · {fmtBytes(stage.zip_size)}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          {(stage?.sql_uploaded || stage?.zip_uploaded) && (
            <Button
              variant="ghost" size="sm" onClick={clearStage}
              data-testid="clear-stage-btn"
              className="text-muted-foreground"
            >
              <Trash2 className="h-3 w-3 mr-1.5" /> Clear staged files
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Step 2 — Run options */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Step 2 · Configure & run</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Dry-run</Label>
              <p className="text-xs text-muted-foreground">Parse & preview counts only. Nothing is written to the database.</p>
            </div>
            <Switch checked={dryRun} onCheckedChange={setDryRun} data-testid="dry-run-toggle" />
          </div>

          <div className={`flex items-center justify-between ${dryRun ? 'opacity-50 pointer-events-none' : ''}`}>
            <div>
              <Label className="text-sm">Truncate before load</Label>
              <p className="text-xs text-muted-foreground">
                Wipe existing business data before inserting legacy data. Required for a clean cutover.
              </p>
            </div>
            <Switch checked={truncate} onCheckedChange={setTruncate} data-testid="truncate-toggle" />
          </div>

          {!dryRun && truncate && (
            <div className="border border-red-500/40 bg-red-50 dark:bg-red-950/20 rounded-md p-3">
              <Label htmlFor="confirm-text" className="text-xs font-medium text-red-700 dark:text-red-300">
                Type <strong>MIGRATE</strong> to confirm. This will permanently delete current business records.
              </Label>
              <Input
                id="confirm-text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="mt-2 max-w-xs"
                placeholder="MIGRATE"
                data-testid="confirm-input"
              />
            </div>
          )}

          <Button
            onClick={start}
            disabled={status.status === 'running' || !stage?.sql_uploaded}
            data-testid="start-migration-btn"
            className="bg-primary"
          >
            <PlayCircle className="h-4 w-4 mr-2" />
            {status.status === 'running'
              ? 'Migration in progress…'
              : dryRun
                ? 'Start dry-run'
                : 'Start live migration'}
          </Button>
        </CardContent>
      </Card>

      {/* Step 3 — Progress */}
      {status.status !== 'idle' && (
        <Card data-testid="migration-progress-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {status.status === 'running' && <Loader2 className="h-4 w-4 animate-spin" />}
              {status.status === 'done' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
              {status.status === 'error' && <XCircle className="h-4 w-4 text-red-600" />}
              Progress
              <Badge variant={status.status === 'error' ? 'destructive' : 'secondary'} className="ml-2">
                {status.status}
              </Badge>
              {status.dry_run && <Badge variant="outline">dry-run</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(status.phases || []).map((p) => (
              <div key={p.name} className="space-y-1" data-testid={`phase-${p.name}`}>
                <div className="flex justify-between text-xs">
                  <span className="font-mono">{p.name}</span>
                  <span className="text-muted-foreground">{p.done} / {p.total || '?'}</span>
                </div>
                <Progress value={p.total ? (p.done / p.total) * 100 : 0} className="h-1.5" />
              </div>
            ))}
            {status.error && (
              <div className="text-xs text-red-600 bg-red-50 dark:bg-red-950/20 p-2 rounded font-mono whitespace-pre-wrap">
                {status.error}
              </div>
            )}
            {status.report && status.status === 'done' && (
              <ReportSummary report={status.report} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Past reports */}
      {reports.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" /> Past migration runs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {reports.map((r) => (
              <div
                key={r.run_id}
                className="flex items-center justify-between text-sm border-b py-2 last:border-0"
                data-testid={`past-run-${r.run_id}`}
              >
                <div>
                  <div className="font-mono text-xs">{r.run_id?.slice(0, 8)}</div>
                  <div className="text-xs text-muted-foreground">
                    {r.started_at ? formatDateTime(r.started_at) : '—'}
                    {r.dry_run && ' · dry-run'}
                    {r.truncate && ' · truncated'}
                    {r.error && <span className="text-red-600"> · failed</span>}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {Object.entries(r.counts || {})
                    .filter(([k]) => k.endsWith('_inserted'))
                    .map(([k, v]) => `${k.replace('_inserted', '')}: ${v}`)
                    .join(' · ')}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const ReportSummary = ({ report }) => {
  const counts = report.counts || {};
  const orphans = report.orphans || {};
  return (
    <div className="border rounded-md p-3 bg-muted/30 text-xs space-y-2" data-testid="report-summary">
      <div className="font-medium text-sm">Final report</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {Object.entries(counts).map(([k, v]) => (
          <div key={k} className="flex justify-between">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-mono">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
          </div>
        ))}
      </div>
      {Object.values(orphans).some((arr) => Array.isArray(arr) && arr.length > 0) && (
        <div className="pt-2 border-t">
          <div className="font-medium text-xs mb-1">Orphans / mismatches</div>
          {Object.entries(orphans).map(([k, v]) => (
            Array.isArray(v) && v.length > 0 && (
              <div key={k} className="text-muted-foreground">
                {k}: {v.length} item{v.length === 1 ? '' : 's'}
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
};

export default MigrationPage;
